import { useFormik } from 'formik';
import { isEqual } from 'lodash-es';
import { useState } from 'react';
import { object, string } from 'yup';
import { type DiscoverTvFilters } from '#types.ts';
import { type Mark } from '../../Form/RangeSlider/types.ts';
import { FormActions } from '../FormActions/index.tsx';
import { TvFilters } from '../TvFilters/index.tsx';
import { createTvRangeSlidersConfig } from '../helpers/createRangeSlidersConfig.ts';
import { transformTvValuestoFilters } from '../helpers/transformValuestoFilters.ts';
import { Form } from '../styled.ts';
import { type Option, type TvFormikValues } from '../types.ts';

export type TvFormikProps = {
  addToShortcuts?: boolean;
  filterOptions: Record<string, Mark[] | Option[]>;
  filtersParam: DiscoverTvFilters;
  initialValues: TvFormikValues;
  setFiltersParam: (value?: DiscoverTvFilters & { shortcutName?: string }, pathnameOverride?: string) => void;
  setOpenFiltersDrawer: (value: boolean) => void;
};

export const TvFormik = ({
  addToShortcuts: initialAddToShortcuts,
  filterOptions,
  filtersParam,
  initialValues,
  setFiltersParam,
  setOpenFiltersDrawer,
}: TvFormikProps) => {
  const formik = useFormik<TvFormikValues & { addToShortcuts: boolean; shortcutName: string }>({
    initialValues: {
      ...initialValues,
      addToShortcuts: initialAddToShortcuts ?? false,
      shortcutName: '',
    },
    onSubmit: ({ addToShortcuts, shortcutName, ...otherValues }) => {
      const newFiltersParam: DiscoverTvFilters = transformTvValuestoFilters(otherValues);

      if (!isEqual(filtersParam, newFiltersParam)) {
        setFiltersParam({ ...(addToShortcuts ? { ...newFiltersParam, shortcutName } : newFiltersParam) }, 'tv');
        setOpenFiltersDrawer(false);
      }
    },
    validationSchema: object({
      shortcutName: string().when('addToShortcuts', {
        is: true,
        // Part of formik API
        // eslint-disable-next-line unicorn/no-thenable
        then: schema => schema.required('Enter a shortcut name'),
      }),
    }),
  });

  const initialRangeSliders = createTvRangeSlidersConfig(initialValues);
  const [rangeSliders, setRangeSliders] = useState(initialRangeSliders);

  return (
    <Form onSubmit={formik.handleSubmit}>
      <TvFilters
        config={{ rangeSliders }}
        filterOptions={filterOptions}
        formik={formik}
        onRangeSliderToggle={(_e, name, checked, defaultValue) => {
          void formik.setFieldValue(name, checked ? defaultValue : []);

          setRangeSliders({
            ...rangeSliders,
            [name]: { ...rangeSliders[name], disabled: !checked },
          });
        }}
      />
      <FormActions
        filtersParam={filtersParam}
        formik={formik}
        setFiltersParam={setFiltersParam}
        setOpenFiltersDrawer={setOpenFiltersDrawer}
      />
    </Form>
  );
};
